import { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { fmtDateTime } from '../../vmsConstants';

// OTP verification for a PENDING_OTP visit.
//
// Two things about this flow are worth stating plainly in the UI, because the
// backend does not enforce either:
//
//  1. Nothing ever delivers the OTP. There is no SMS and no push — the code is
//     returned in the create-visit response and is visible only to whoever
//     created the visit. So the gate operator has to read it out. It is shown
//     here behind a reveal, never rendered where a visitor could see it, and
//     never logged.
//  2. There is no attempt counter and no lockout. A wrong code just comes back
//     400 and can be retried until the code expires, so the expiry is the only
//     real limit and is surfaced prominently.
const VerifyOtpDialog = ({ visit, busy = false, onCancel, onConfirm }) => {
    const [otp, setOtp] = useState('');
    const [revealCode, setRevealCode] = useState(false);

    useEffect(() => {
        setOtp('');
        setRevealCode(false);
    }, [visit?.visit_id]);

    if (!visit) return null;

    const expired = visit.otp_expires_at && new Date(visit.otp_expires_at) < new Date();
    const canSubmit = otp.length === 6 && !busy && !expired;

    return (
        <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4">
            <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-md tw-overflow-hidden">
                <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-flex tw-items-start tw-gap-3">
                    <div className="tw-w-9 tw-h-9 tw-rounded-lg tw-bg-violet-50 tw-text-violet-600 tw-flex tw-items-center tw-justify-center tw-shrink-0">
                        <Icon icon="mdi:shield-key-outline" className="tw-text-lg" />
                    </div>
                    <div>
                        <h3 className="tw-mb-0 tw-text-sm tw-font-bold tw-text-gray-800">Verify visitor OTP</h3>
                        <p className="tw-mb-0 tw-text-[11px] tw-text-gray-500 tw-mt-0.5">
                            {visit.visit_code} · {visit.visitor?.name || visit.visitor_name}
                        </p>
                    </div>
                </div>

                <div className="tw-p-5 tw-space-y-4">
                    {expired ? (
                        <div className="tw-flex tw-items-start tw-gap-2 tw-px-3 tw-py-2.5 tw-rounded-md tw-bg-rose-50 tw-border tw-border-rose-100 tw-text-[11px] tw-text-rose-800">
                            <Icon icon="mdi:timer-off-outline" className="tw-text-sm tw-shrink-0 tw-mt-px" />
                            <span>
                                This code expired on {fmtDateTime(visit.otp_expires_at)}. It cannot be verified —
                                the visit has to be logged again to issue a new one.
                            </span>
                        </div>
                    ) : visit.otp_expires_at && (
                        <p className="tw-mb-0 tw-flex tw-items-center tw-gap-1.5 tw-text-[11px] tw-text-gray-600">
                            <Icon icon="mdi:clock-outline" className="tw-text-sm tw-text-gray-400" />
                            Valid until {fmtDateTime(visit.otp_expires_at)}
                        </p>
                    )}

                    {/* Operator-only. The visitor never receives this code by any
                        channel, so the person at the gate reads it out. */}
                    {visit.otp_code && (
                        <div className="tw-px-3 tw-py-2.5 tw-rounded-md tw-bg-amber-50 tw-border tw-border-amber-100">
                            <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                                <span className="tw-text-[10px] tw-uppercase tw-tracking-wide tw-font-semibold tw-text-amber-800">
                                    Code on file — staff only
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setRevealCode(r => !r)}
                                    className="tw-flex tw-items-center tw-gap-1 tw-text-[10px] tw-font-semibold tw-text-amber-800 hover:tw-text-amber-900"
                                >
                                    <Icon icon={revealCode ? 'mdi:eye-off-outline' : 'mdi:eye-outline'} className="tw-text-sm" />
                                    {revealCode ? 'Hide' : 'Reveal'}
                                </button>
                            </div>
                            <p className="tw-mb-0 tw-mt-1 tw-font-mono tw-text-lg tw-font-bold tw-tracking-[0.3em] tw-text-amber-900">
                                {revealCode ? visit.otp_code : '••••••'}
                            </p>
                            <p className="tw-mb-0 tw-mt-1 tw-text-[10px] tw-text-amber-800">
                                Not sent to the visitor by SMS or app — read it out at the gate. Do not show this screen to the visitor.
                            </p>
                        </div>
                    )}

                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-semibold tw-text-gray-700 tw-mb-1">
                            Enter the 6-digit code <span className="tw-text-rose-500">*</span>
                        </label>
                        <input
                            type="text"
                            inputMode="numeric"
                            autoComplete="off"
                            value={otp}
                            // Digits only, exactly six — the endpoint rejects
                            // anything else before it even checks the value.
                            onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            disabled={expired}
                            placeholder="000000"
                            className="tw-w-full tw-px-3 tw-py-2.5 tw-text-center tw-text-lg tw-font-mono tw-tracking-[0.4em] tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-violet-500 disabled:tw-bg-gray-50"
                        />
                        <p className="tw-mb-0 tw-text-[10px] tw-text-gray-400 tw-mt-1">{otp.length}/6</p>
                    </div>

                    <div className="tw-flex tw-justify-end tw-gap-2">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={() => onConfirm(otp)}
                            disabled={!canSubmit}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-violet-600 hover:tw-bg-violet-700 tw-rounded-md disabled:tw-opacity-50"
                        >
                            <Icon icon="mdi:shield-check-outline" className="tw-text-sm" />
                            {busy ? 'Verifying...' : 'Verify'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VerifyOtpDialog;
